import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { ListResource } from '../../../../components/ListResource/ListResource';
import { setRatingShowInfo } from '../utils/setRatingShowInfo';
import { useOwnerContext } from '../../../../contexts/OwnerContext/OwnerContext';
import {
  createConditionalRatingColumns,
  createStandardRatingColumns
} from '../../../../utils/createRatingColumns';
import { toParticipantOption } from '../../../../utils/reviewParticipants';

const ListRatings = ({
  form,
  setSupervisionRating,
  setShowReviewDrawer,
  setRefreshData,
  setReviewDrawerOpen,
  refreshData,
  setReviewData,
  reviewData,
  setEditReviewDrawer
}) => {
  const { t } = useTranslation();
  const { owner, reviewActiveKey, questionnaireType } = useOwnerContext();
  const userId = owner?.user?._id ?? owner?.user;

  const setExtraQuery = () => {
    if (reviewActiveKey === 'received_reviews') {
      return `received_reviews=${userId}`;
    }
    return `author.id=${userId}`;
  };

  useEffect(() => {
    setExtraQuery();
    setRefreshData(!refreshData);
  }, [reviewActiveKey]);

  const setDataForCreateForm = () => {
    form.resetFields();
    setShowReviewDrawer(false);
    form.setFieldsValue({
      questionnaire_type: 'users:OWNER',
      author: { id: toParticipantOption(owner) }
    });
  };

  useEffect(() => {
    if (Object.keys(reviewData).length) {
      setRatingShowInfo({
        setSupervisionRating,
        reviewData,
        reviewActiveKey,
        form,
        questionnaireType
      });
    }
  }, [reviewData]);

  // Nothing to list before the owner and its account are loaded: an
  // undefined id would reach the API as the string "undefined".
  if (!userId) return null;

  const columns = [
    ...createConditionalRatingColumns({ t, reviewActiveKey }),
    ...createStandardRatingColumns({
      t,
      setEditReviewDrawer,
      setShowReviewDrawer,
      setReviewData,
      setReviewDrawerOpen
    })
  ];

  return (
    <ListResource
      forceRefresh={refreshData}
      resourceName="ratings"
      columns={columns}
      extraQuery={setExtraQuery()}
      populate="author.id.user,receiver.id.user,"
      customActionColumn
      withPageHeader={false}
      onDoubleClickAction={{
        action: (record) => {
          setEditReviewDrawer(false);
          setShowReviewDrawer(true);
          setReviewData(record);
          setReviewDrawerOpen(true);
        }
      }}
      withCreateButton={false}
      withCustomCreateButton={reviewActiveKey === 'left_reviews'}
      customCreateButtonAction={() => {
        setShowReviewDrawer(false);
        setEditReviewDrawer(false);
        setReviewDrawerOpen(true);
        setDataForCreateForm();
      }}
    />
  );
};

export default ListRatings;

ListRatings.propTypes = {
  form: PropTypes.shape({
    setFieldsValue: PropTypes.func,
    resetFields: PropTypes.func
  }).isRequired,
  setSupervisionRating: PropTypes.func,
  setShowReviewDrawer: PropTypes.func,
  setRefreshData: PropTypes.func,
  refreshData: PropTypes.bool,
  setReviewData: PropTypes.func,
  reviewData: PropTypes.shape({}),
  setEditReviewDrawer: PropTypes.func,
  setReviewDrawerOpen: PropTypes.func
};

ListRatings.defaultProps = {
  reviewData: {},
  setSupervisionRating: () => {},
  setShowReviewDrawer: () => {},
  setRefreshData: () => {},
  setReviewData: () => {},
  setEditReviewDrawer: () => {},
  refreshData: false,
  setReviewDrawerOpen: () => {}
};
